import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";

const LOGO_URL = "/logo.png";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Trim inputs to remove any whitespace from mobile keyboards
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    
    if (!trimmedEmail || !trimmedPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // Log password length for debugging (not the actual password)
      console.log(`Attempting login with email: ${trimmedEmail}, password length: ${trimmedPassword.length}`);
      
      const result = await login(trimmedEmail, trimmedPassword);
      toast.success("Welcome back!");
      
      // Navigate based on role - admin, super_admin and staff go to admin dashboard
      if (["admin", "super_admin", "staff"].includes(result.user.role)) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      console.error("Password length was:", trimmedPassword.length);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex justify-center mb-8">
            <img src={LOGO_URL} alt="Trish Drives Honda" className="h-16" />
          </Link>
          
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-[#E5E7EB]/50">
            <h1 className="font-heading text-3xl font-bold text-[#2C2420] text-center mb-2">
              Welcome Back
            </h1>
            <p className="text-[#6B5E55] text-center mb-8">
              Sign in to your account
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on">
              <div className="form-group">
                <Label className="form-label">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B5E55]" />
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 form-input"
                    data-testid="input-email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <div className="flex items-center justify-between mb-2">
                  <Label className="form-label mb-0">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-[#8D7B68] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B5E55]" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 pr-12 form-input"
                    data-testid="input-password"
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B5E55] hover:text-[#2C2420]"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {password.length > 0 && (
                  <p className="text-xs text-[#6B5E55] mt-1">
                    {password.length} characters entered
                  </p>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
            
            <p className="text-center text-[#6B5E55] mt-8">
              For account access, please contact an administrator.
            </p>
          </div>
        </div>
      </div>
      
      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_trish-honda-crm/artifacts/u4x6w3c4_Web3%20copy.webp"
          alt="Trish"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F9F7F2] to-transparent"></div>
      </div>
    </div>
  );
};

export default LoginPage;
